import type { Member, Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { menu, selectionToolbar } from "../../ui/primitives.ts";
import { createBulkActionController, type BulkUndoField } from "./bulk-action-controller.ts";
import { openBulkEditDialog, openBulkMoveDialog, type BulkDialogCapabilities } from "./bulk-dialog-controller.ts";

export interface BulkTaskRuntimeCapabilities<Snapshot> {
  tasks(): Todo[];
  selectedIds(): Set<string>;
  setSelectedIds(ids: Set<string>): void;
  setSelectionMode(active: boolean): void;
  activeRoot(): string | null;
  canEdit(id: string): boolean;
  blockers(todo: Todo): Todo[];
  approvalRequired?(todo: Todo): boolean;
  complete(todo: Todo): Promise<string | null>;
  mutate(id: string, update: (todo: Todo) => void): Promise<unknown>;
  mutateBatch?(items: Array<{ id: string; rootId: string; mutate(todo: Todo): void }>): Promise<unknown>;
  snapshot(todo: Todo): Snapshot;
  confirmDelete(count: number): Promise<boolean>;
  render(): Promise<void>;
  focusSelection(id: string): void;
  taskUndo(message: string, rootId: string, before: Snapshot[], fields: BulkUndoField[], generatedIds: string[]): void;
  toast(message: string): void;
  sync(): void;
  now(): number;
  roots(): Root[];
  categories(): Promise<Category[]>;
  members(): Member[];
  membersFor?(todo: Todo): Member[];
  change(id: string, rootId: string, update: (todo: Todo) => void): Promise<void>;
  randomId(): string;
  cloneComments(todo: Todo): Todo["comments"];
  clearSelection(): Promise<void>;
  actionUndo(message: string, action: () => Promise<void>): void;
  icon(name: string): string;
  online?(): boolean;
  verifyFamilyTransfer?(tasks: Todo[], destinationRootId: string, destinationCategoryId: string | null): Promise<boolean>;
}

export function createBulkTaskRuntime<Snapshot>(capabilities: BulkTaskRuntimeCapabilities<Snapshot>) {
  const actions = createBulkActionController<Snapshot>({
    tasks: capabilities.tasks,
    selectedIds: capabilities.selectedIds,
    setSelectedIds: capabilities.setSelectedIds,
    setSelectionMode: capabilities.setSelectionMode,
    activeRoot: capabilities.activeRoot,
    canEdit: capabilities.canEdit,
    blockers: capabilities.blockers,
    approvalRequired: capabilities.approvalRequired,
    complete: capabilities.complete,
    mutate: capabilities.mutate,
    mutateBatch: capabilities.mutateBatch,
    snapshot: capabilities.snapshot,
    confirmDelete: capabilities.confirmDelete,
    render: capabilities.render,
    focusSelection: capabilities.focusSelection,
    undo: capabilities.taskUndo,
    toast: capabilities.toast,
    sync: capabilities.sync,
    now: capabilities.now,
  });
  const dialogs = (): BulkDialogCapabilities<Snapshot> => ({
    selected: () => capabilities.tasks().filter(todo => capabilities.selectedIds().has(todo.id) && capabilities.canEdit(todo.id)),
    tasks: capabilities.tasks,
    canEdit: capabilities.canEdit,
    activeRoot: capabilities.activeRoot,
    roots: capabilities.roots,
    categories: capabilities.categories,
    members: capabilities.members,
    membersFor: capabilities.membersFor,
    snapshot: capabilities.snapshot,
    mutate: capabilities.mutate,
    mutateBatch: capabilities.mutateBatch,
    change: capabilities.change,
    randomId: capabilities.randomId,
    cloneComments: capabilities.cloneComments,
    finish: actions.finish,
    clearSelection: capabilities.clearSelection,
    undo: capabilities.actionUndo,
    sync: capabilities.sync,
    icon: capabilities.icon,
    now: capabilities.now,
    toast: capabilities.toast,
    online: capabilities.online,
    verifyFamilyTransfer: capabilities.verifyFamilyTransfer,
  });

  return {
    setSelected: actions.setSelected,
    apply: actions.apply,
    finish: actions.finish,
    move: (): Promise<void> => openBulkMoveDialog(dialogs()),
    edit: (): Promise<void> => openBulkEditDialog(dialogs()),
    toolbar: (): string => {
      const selected = capabilities.tasks().filter(todo => capabilities.selectedIds().has(todo.id) && capabilities.canEdit(todo.id));
      const count = selected.length;
      const canMove = count > 0 && new Set(selected.map(todo => todo.rootId)).size === 1;
      const item = (action: string, label: string, danger = false) => `<button type="button" role="menuitem" class="${danger ? "is-danger" : ""}" data-action="${action}" ${count ? "" : "disabled"}>${label}</button>`;
      const more = menu("More selection actions", capabilities.icon("ellipsis"), `<button type="button" role="menuitem" data-action="select-visible-tasks">Select all</button>${selected.some(todo => todo.completed) ? item("bulk-reopen", "Reopen") : ""}${selected.some(todo => !todo.pinned) ? item("bulk-pin", "Pin") : ""}${selected.some(todo => todo.pinned) ? item("bulk-unpin", "Unpin") : ""}${item("bulk-edit", "Edit…")}${item("bulk-archive", "Archive")}${item("bulk-delete", "Trash", true)}`, "selection-more");
      return selectionToolbar(count, `<button type="button" class="text-button" data-action="bulk-complete" ${selected.some(todo => !todo.completed) ? "" : "disabled"}>Complete</button><button type="button" class="text-button" data-action="bulk-move" ${canMove ? "" : 'disabled title="Select tasks from one space to move them together"'}>Move</button>${more}<button type="button" class="text-button" data-action="toggle-selection-mode" aria-label="Exit selection mode">Done</button>`);
    },
  };
}
